import React, { useState, useEffect } from 'react';
import './TechnicalExpertise.css';
import techLogos from '../data/tech_logos.json';

const TechLogoItem = ({ tech, logoUrl }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const isImageUrl = logoUrl && (logoUrl.startsWith('http') || logoUrl.endsWith('.svg'));
  
  return (
    <div className="tech-logo-item">
      <div className="tech-logo">
        {isImageUrl && !imageError ? (
          <>
            <img 
              src={logoUrl} 
              alt={tech}
              className="tech-logo-img"
              onError={() => setImageError(true)}
              onLoad={() => setImageLoaded(true)}
              style={{ display: imageLoaded ? 'block' : 'none' }}
            />
            {!imageLoaded && !imageError && (
              <span className="tech-logo-text">
                {tech.substring(0, 3).toUpperCase()}
              </span>
            )}
          </>
        ) : (
          <span className="tech-logo-text">
            {tech.substring(0, 3).toUpperCase()}
          </span>
        )}
      </div>
      <div className="tech-name">{tech}</div>
    </div>
  );
};

const TechnicalExpertise = ({ data }) => {
  const allTechnologies = Object.values(data.technicalExpertise).flat();
  const uniqueTechnologies = [...new Set(allTechnologies.map(t => t.trim()))];
  
  const duplicatedTechnologies = [...uniqueTechnologies, ...uniqueTechnologies, ...uniqueTechnologies];

  const [currentIndex, setCurrentIndex] = useState(uniqueTechnologies.length);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const itemsPerView = 5;

  const nextSlide = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => {
      const next = prev + 1;
      if (next >= uniqueTechnologies.length * 2) {
        setTimeout(() => {
          setIsTransitioning(false);
          setCurrentIndex(uniqueTechnologies.length);
          setTimeout(() => setIsTransitioning(true), 50);
        }, 800);
        return uniqueTechnologies.length * 2;
      }
      return next;
    });
  };

  const prevSlide = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => {
      const next = prev - 1;
      if (next < uniqueTechnologies.length) {
        setIsTransitioning(false);
        const jumpTo = uniqueTechnologies.length * 2 - 1;
        setTimeout(() => {
          setIsTransitioning(true);
          setCurrentIndex(jumpTo);
        }, 50);
        return jumpTo;
      }
      return next;
    });
  };

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = prev + 1;
        if (next > uniqueTechnologies.length * 2) {
          setTimeout(() => {
            setIsTransitioning(false);
            setCurrentIndex(uniqueTechnologies.length);
            setTimeout(() => setIsTransitioning(true), 50);
          }, 800);
          return uniqueTechnologies.length * 2;
        }
        return next;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, uniqueTechnologies.length]);

  const getTechnologyLogo = (tech) => {
    if (techLogos[tech] && techLogos[tech].url) {
      return techLogos[tech].url;
    }
    return null;
  };

  return (
    <section id="expertise" className="technical-expertise">
      <div className="container">
        <h2 className="section-title">Technical Expertise</h2>
        <p className="section-subtitle">
          Technologies and tools I work with
        </p>
        
        <div 
          className="tech-slider-container"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="tech-slider-wrapper">
            <div 
              className={`tech-slider-track ${isTransitioning ? '' : 'no-transition'}`}
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
            >
              {duplicatedTechnologies.map((tech, index) => (
                <TechLogoItem 
                  key={index} 
                  tech={tech} 
                  logoUrl={getTechnologyLogo(tech)} 
                />
              ))}
            </div>
          </div>

          <button 
            className="tech-slider-btn tech-slider-btn-prev" 
            onClick={prevSlide}
            aria-label="Previous technology"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <button 
            className="tech-slider-btn tech-slider-btn-next" 
            onClick={nextSlide}
            aria-label="Next technology"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default TechnicalExpertise;
